import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguageStore, translations, type Language } from "../store/languageStore";
import { useMatchStore } from "../store/matchStore";
import { useNeedStore } from "../store/needStore";
import { useUserDataStore } from "../store/userDataStore";
import StepIndicator from "../components/StepIndicator";
import { supabase } from "../lib/supabase";
import type { AIProgram } from "../api/gpt";
import { analytics } from "../utils/analytics";

const steps = [
  {
    number: 1,
    label: translations.stepLabels.selectNeed,
  },
  {
    number: 2,
    label: translations.stepLabels.matchSupport,
  },
  {
    number: 3,
    label: translations.stepLabels.exploreAid,
  },
];

// Helper function to get translation from multilingual object

const Match: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLanguage } = useLanguageStore();
  const { selectedNeed } = useNeedStore();
  const { zipCode } = useUserDataStore();
  const { setMatchResults, setFormData } = useMatchStore();
  const [isLoading, setIsLoading] = useState(false);
  const [, setShowAILoader] = useState(false);

  const [formData, setLocalFormData] = useState({
    age: "",
    monthlyIncome: "",
    householdSize: "1",
    employmentStatus: "employed",
    immigrationStatus: "citizen",
    receivesAid: false,
    hasDocuments: false,
    speaksEnglish: true,
  });

  useEffect(() => {
    analytics.track("match_page_viewed", "navigation", {
      need: selectedNeed,
      language: selectedLanguage,
    });
  }, [selectedNeed, selectedLanguage]);

  const messages = {
    EN: {
      back: "Back",
      findMatches: "Find Your Best Program Matches",
      answerQuestions:
        "Answer a few questions to help us find the most suitable aid programs for your situation.",
      age: "Your Age",
      monthlyIncome: "Monthly Household Income",
      selectIncome: "Select income range",
      householdSize: "Household Size",
      employmentStatus: "Employment Status",
      employed: "Employed",
      unemployed: "Unemployed",
      student: "Student",
      retired: "Retired",
      status: "Status",
      citizen: "US Citizen",
      permanent: "Permanent Resident",
      other: "Other Status",
      additionalInfo: "Additional Information",
      receivingAid:
        "Currently receiving other government aid (SNAP, Medicaid, etc.)",
      hasDocuments:
        "Have required documents ready (ID, proof of income, bills)",
      speaksEnglish: "Comfortable communicating in English",
      findingBestFit: "Finding the best fit for you… powered by AI",
      findMyMatches: "Find My Matches",
      zipCodeRequired: "Please set your zip code in the home screen first",
      loadingTime: "Please wait 15-20 seconds for the AI to find the best programs for you."
    },
    ES: {
      back: "Atrás",
      findMatches: "Encuentra Tus Mejores Coincidencias de Programas",
      answerQuestions:
        "Responde algunas preguntas para ayudarnos a encontrar los programas de ayuda más adecuados para tu situación.",
      age: "Tu Edad",
      monthlyIncome: "Ingresos Mensuales del Hogar",
      selectIncome: "Selecciona el rango de ingresos",
      householdSize: "Tamaño del Hogar",
      employmentStatus: "Estado de Empleo",
      employed: "Empleado",
      unemployed: "Desempleado",
      student: "Estudiante",
      retired: "Jubilado",
      status: "Estado",
      citizen: "Ciudadano Estadounidense",
      permanent: "Residente Permanente",
      other: "Otro Estado",
      additionalInfo: "Información Adicional",
      receivingAid:
        "Actualmente recibiendo otra ayuda gubernamental (SNAP, Medicaid, etc.)",
      hasDocuments:
        "Tener documentos requeridos listos (ID, comprobante de ingresos, facturas)",
      speaksEnglish: "Cómodo comunicándose en inglés",
      findingBestFit: "Encontrando la mejor coincidencia para ti... impulsado por IA",
      findMyMatches: "Encontrar Mis Coincidencias",
      zipCodeRequired: "Por favor establece tu código postal en la pantalla de inicio primero",
      loadingTime: "Espere de 15 a 20 segundos para que la IA encuentre los mejores programas para usted."
    },
    ZH: {
      back: "返回",
      findMatches: "找到最适合您的项目",
      answerQuestions:
        "回答几个问题，帮助我们为您的具体情况找到最合适的援助项目。",
      age: "您的年龄",
      monthlyIncome: "月家庭收入",
      selectIncome: "选择收入范围",
      householdSize: "家庭人数",
      employmentStatus: "就业状况",
      employed: "在职",
      unemployed: "失业",
      student: "学生",
      retired: "退休",
      status: "身份",
      citizen: "美国公民",
      permanent: "永久居民",
      other: "其他身份",
      additionalInfo: "附加信息",
      receivingAid: "目前正在接受其他政府援助（SNAP、Medicaid等）",
      hasDocuments: "已准备好所需文件（身份证、收入证明、账单）",
      speaksEnglish: "能够用英语交流",
      findingBestFit: "正在为您找到最佳匹配...由AI驱动",
      findMyMatches: "查找我的匹配",
      zipCodeRequired: "请先在主页设置您的邮政编码",
      loadingTime: "请等待15-20秒，以便AI为您找到最佳程序。"
    },
    VI: {
      back: "Quay lại",
      findMatches: "Tìm Chương Trình Phù Hợp Nhất",
      answerQuestions:
        "Trả lời một vài câu hỏi để giúp chúng tôi tìm các chương trình hỗ trợ phù hợp nhất với tình hình của bạn.",
      age: "Tuổi của bạn",
      monthlyIncome: "Thu nhập hàng tháng của hộ gia đình",
      selectIncome: "Chọn phạm vi thu nhập",
      householdSize: "Số người trong gia đình",
      employmentStatus: "Tình trạng việc làm",
      employed: "Đang làm việc",
      unemployed: "Thất nghiệp",
      student: "Sinh viên",
      retired: "Đã nghỉ hưu",
      status: "Tình trạng",
      citizen: "Công dân Hoa Kỳ",
      permanent: "Thường trú nhân",
      other: "Tình trạng khác",
      additionalInfo: "Thông tin bổ sung",
      receivingAid:
        "Hiện đang nhận hỗ trợ khác từ chính phủ (SNAP, Medicaid, v.v.)",
      hasDocuments:
        "Đã có sẵn các giấy tờ cần thiết (ID, bằng chứng thu nhập, hóa đơn)",
      speaksEnglish: "Thoải mái giao tiếp bằng tiếng Anh",
      findingBestFit: "Đang tìm sự phù hợp nhất cho bạn... được hỗ trợ bởi AI",
      findMyMatches: "Tìm các chương trình phù hợp",
      zipCodeRequired: "Vui lòng đặt mã ZIP của bạn trong màn hình chính trước",
      loadingTime: "Vui lòng đợi 15-20 giây để AI tìm ra chương trình tốt nhất cho bạn."
    },
    FIL: {
      back: "Bumalik",
      findMatches: "Hanapin ang Pinakamahusay na Programa para sa Iyo",
      answerQuestions:
        "Sagutin ang ilang mga tanong para matulungan kaming mahanap ang pinakangkop na programa ng tulong para sa inyong sitwasyon.",
      age: "Inyong Edad",
      monthlyIncome: "Buwanang Kita ng Sambahayan",
      selectIncome: "Piliin ang saklaw ng kita",
      householdSize: "Laki ng Sambahayan",
      employmentStatus: "Katayuan sa Trabaho",
      employed: "May Trabaho",
      unemployed: "Walang Trabaho",
      student: "Estudyante",
      retired: "Retirado",
      status: "Katayuan",
      citizen: "Mamamayan ng US",
      permanent: "Permanent Resident",
      other: "Ibang Katayuan",
      additionalInfo: "Karagdagang Impormasyon",
      receivingAid:
        "Kasalukuyang tumatanggap ng ibang tulong mula sa gobyerno (SNAP, Medicaid, atbp.)",
      hasDocuments:
        "May handang kinakailangang dokumento (ID, patunay ng kita, mga bill)",
      speaksEnglish: "Komportableng makipag-usap sa Ingles",
      findingBestFit:
        "Naghahanap ng pinakamahusay na tugma para sa inyo... pinapagana ng AI",
      findMyMatches: "Hanapin ang Aking mga Tugma",
      zipCodeRequired: "Mangyaring itakda ang iyong ZIP code sa home screen muna",
      loadingTime: "Mangyaring maghintay ng 15-20 segundo para mahanap ng AI ang pinakamahusay na mga programa para sa iyo."
    },
    KO: {
      back: "뒤로",
      findMatches: "최고의 프로그램 매치 찾기",
      answerQuestions:
        "귀하의 상황에 가장 적합한 원조 프로그램을 찾는 데 도움이 되도록 몇 가지 질문에 답해 주세요.",
      age: "귀하의 나이",
      monthlyIncome: "월 가구 소득",
      selectIncome: "소득 범위 선택",
      householdSize: "가구 규모",
      employmentStatus: "고용 상태",
      employed: "고용됨",
      unemployed: "실업",
      student: "학생",
      retired: "은퇴",
      status: "상태",
      citizen: "미국 시민",
      permanent: "영주권자",
      other: "기타 상태",
      additionalInfo: "추가 정보",
      receivingAid: "현재 다른 정부 원조를 받고 있음 (SNAP, Medicaid 등)",
      hasDocuments: "필요한 서류 준비됨 (ID, 소득 증명, 청구서)",
      speaksEnglish: "영어로 의사소통하는 것이 편함",
      findingBestFit: "귀하에게 가장 적합한 매치를 찾는 중... AI로 구동",
      findMyMatches: "내 매치 찾기",
      zipCodeRequired: "먼저 홈 화면에서 우편번호를 설정해 주세요",
      loadingTime: "AI가 귀하에게 가장 적합한 프로그램을 찾는 데 15-20초 정도 기다려 주십시오."
    },
    AR: {
      back: "رجوع",
      findMatches: "ابحث عن أفضل البرامج المناسبة لك",
      answerQuestions:
        "أجب على بعض الأسئلة لمساعدتنا في العثور على برامج المساعدة الأنسب لحالتك.",
      age: "عمرك",
      monthlyIncome: "الدخل الشهري للأسرة",
      selectIncome: "اختر نطاق الدخل",
      householdSize: "حجم الأسرة",
      employmentStatus: "الحالة الوظيفية",
      employed: "موظف",
      unemployed: "عاطل عن العمل",
      student: "طالب",
      retired: "متقاعد",
      status: "الحالة",
      citizen: "مواطن أمريكي",
      permanent: "مقيم دائم",
      other: "حالة أخرى",
      additionalInfo: "معلومات إضافية",
      receivingAid: "يتلقى حالياً مساعدات حكومية أخرى (SNAP، Medicaid، إلخ)",
      hasDocuments: "المستندات المطلوبة جاهزة (الهوية، إثبات الدخل، الفواتير)",
      speaksEnglish: "مرتاح للتواصل باللغة الإنجليزية",
      findingBestFit: "جارٍ البحث عن أفضل تطابق لك... مدعوم بالذكاء الاصطناعي",
      findMyMatches: "البحث عن التطابقات",
      zipCodeRequired: "يرجى تعيين الرمز البريدي في الشاشة الرئيسية أولاً",
      loadingTime: "يرجى الانتظار لمدة 15-20 ثانية حتى يجد الذكاء الاصطناعي أفضل البرامج لك."
    },
  };

  const currentMessages = messages[selectedLanguage as keyof typeof messages] || messages.EN;

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setLocalFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zipCode || !zipCode.trim()) {
      alert(currentMessages.zipCodeRequired);
      return;
    }
    setIsLoading(true);
    setShowAILoader(true);
    analytics.track("match_form_submitted", "conversion", {
      need: selectedNeed,
      age: formData.age,
      income_range: getIncomeRange(formData.monthlyIncome),
      household_size: formData.householdSize,
      employment_status: formData.employmentStatus,
      immigration_status: formData.immigrationStatus,
      language: selectedLanguage,
      zip_code: zipCode,
    });
    try {
      // Generate AI programs based on zip code, need, and selected language
      const { data, error } = await supabase.functions.invoke('ai-proxy', {
        body: {
          type: 'program_recommendations',
          data: {
            zipCode,
            need: selectedNeed,
            radius: 25, // 25 mile radius
            language: selectedLanguage
          }
        }
      });

      if (error) {
        throw new Error(`Failed to get program recommendations: ${error.message}`);
      }

      if (!data || !data.programs) {
        throw new Error('No programs returned from AI service');
      }

      const aiPrograms = data.programs;
      // Convert AI programs to match results format
      const matchResults = calculateMatchResults(aiPrograms);
      setMatchResults(matchResults);
      setFormData(formData);
      analytics.track("match_results_generated", "conversion", {
        need: selectedNeed,
        results_count: matchResults.length,
        language: selectedLanguage,
        zip_code: zipCode,
      });
      setShowAILoader(false);
      navigate("/results");
    } catch (error) {
      console.error("Error generating matches:", error);
      setShowAILoader(false);
      alert("Failed to generate matches. Please try again.");
    }
  };

  const getIncomeRange = (income: string) => {
    const incomeNum = parseInt(income);
    if (incomeNum < 1000) return "under_1k";
    if (incomeNum < 2000) return "1k_2k";
    if (incomeNum < 3000) return "2k_3k";
    if (incomeNum < 5000) return "3k_5k";
    return "over_5k";
  };

  const calculateMatchResults = (aiPrograms: AIProgram[]) => {
    const age = parseInt(formData.age);
    const income = parseInt(formData.monthlyIncome);
    const householdSize = parseInt(formData.householdSize);
    const isCitizen =
      formData.immigrationStatus === "citizen" ||
      formData.immigrationStatus === "permanent";

    return aiPrograms.map((program, index) => {
      const units = {
        years: {
          EN: "years",
          ES: "años",
          ZH: "岁",
          VI: "tuổi",
          FIL: "taon",
          KO: "세",
          AR: "سنوات",
        },
        month: {
          EN: "month",
          ES: "mes",
          ZH: "月",
          VI: "tháng",
          FIL: "buwan",
          KO: "월",
          AR: "شهر",
        },
      } as const;

      const langs: Language[] = ["EN", "ES", "ZH", "VI", "FIL", "KO", "AR"];

      const statusValueMap = langs.reduce((acc, lang) => {
        acc[lang] = {
          citizen: messages[lang].citizen,
          permanent: messages[lang].permanent,
          other: messages[lang].other,
        };
        return acc;
      }, {} as Record<Language, { citizen: string; permanent: string; other: string }>);

      // Enhanced match criteria with more sophisticated scoring
      const matchCriteria = [
        {
          name: {
            EN: "Age Requirement",
            ES: "Requisito de Edad",
            ZH: "年龄要求",
            VI: "Yêu Cầu Tuổi",
            FIL: "Kailangan sa Edad",
            KO: "연령 요건",
            AR: "متطلب العمر",
          },
          passed: age >= 18 && age <= 65,
          value: langs.reduce((acc, lang) => {
            acc[lang] = `${age} ${units.years[lang]}`;
            return acc;
          }, {} as Record<Language, string>),
        },
        {
          name: {
            EN: "Income Limit",
            ES: "Límite de Ingresos",
            ZH: "收入上限",
            VI: "Giới Hạn Thu Nhập",
            FIL: "Limitasyon sa Kita",
            KO: "소득 한도",
            AR: "حد الدخل",
          },
          passed: income < 50000,
          value: langs.reduce((acc, lang) => {
            acc[lang] = `$${income}/${units.month[lang]}`;
            return acc;
          }, {} as Record<Language, string>),
        },
        {
          name: {
            EN: "Household Size",
            ES: "Tamaño del Hogar",
            ZH: "家庭人数",
            VI: "Quy Mô Hộ Gia Đình",
            FIL: "Laki ng Sambahayang",
            KO: "가구 규모",
            AR: "حجم الأسرة",
          },
          passed: householdSize <= 8,
          value: langs.reduce((acc, lang) => {
            acc[lang] = householdSize.toString();
            return acc;
          }, {} as Record<Language, string>),
        },
        {
          name: {
            EN: "Status",
            ES: "Estado",
            ZH: "身份",
            VI: "Tình Trạng",
            FIL: "Katayuan",
            KO: "신분",
            AR: "الوضع",
          },
          passed: isCitizen,
          value: langs.reduce((acc, lang) => {
            acc[lang] =
              statusValueMap[lang][
                formData.immigrationStatus as "citizen" | "permanent" | "other"
              ];
            return acc;
          }, {} as Record<Language, string>),
        },
        {
          name: {
            EN: "Documentation",
            ES: "Documentación",
            ZH: "文件",
            VI: "Tài Liệu",
            FIL: "Dokumentasyon",
            KO: "서류",
            AR: "الوثائق",
          },
          passed: formData.hasDocuments,
        },
      ];

      // Enhanced match percentage calculation
      let matchPercentage = 0;
      
      // Base criteria scoring (60% of total)
      const baseScore = matchCriteria.reduce(
        (acc, criteria) => acc + (criteria.passed ? 12 : 0), // 12% per criteria (60% total)
        0
      );
      matchPercentage += baseScore;

      // Distance-based scoring (20% of total)
      const maxDistance = 25; // 25 mile radius
      const distanceScore = Math.max(0, 20 - (program.distance / maxDistance) * 20);
      matchPercentage += distanceScore;

      // Language compatibility (10% of total)
      const userSpeaksEnglish = formData.speaksEnglish;
      const programSupportsEnglish = program.languages.includes('en');
      const languageScore = (userSpeaksEnglish && programSupportsEnglish) ? 10 : 5;
      matchPercentage += languageScore;

      // Employment status bonus (10% of total)
      let employmentBonus = 0;
      if (formData.employmentStatus === 'unemployed' && selectedNeed === 'jobs') {
        employmentBonus = 10; // Perfect match for job programs
      } else if (formData.employmentStatus === 'employed' && selectedNeed !== 'jobs') {
        employmentBonus = 5; // Good for non-job programs
      }
      matchPercentage += employmentBonus;

      // Income-based adjustments
      if (income < 2000 && selectedNeed === 'rent') {
        matchPercentage += 5; // Higher priority for low-income rent assistance
      }
      if (income < 1500 && selectedNeed === 'food') {
        matchPercentage += 5; // Higher priority for low-income food assistance
      }

      // Household size considerations
      if (householdSize > 4 && (selectedNeed === 'rent' || selectedNeed === 'food')) {
        matchPercentage += 3; // Larger households get priority for basic needs
      }

      return {
        id: parseInt(program.id) || index + 1,
        type: selectedNeed,
        name: {
          EN: program.name,
          ES: program.name,
          ZH: program.name,
          VI: program.name,
          FIL: program.name,
          KO: program.name,
          AR: program.name,
        },
        description: {
          EN: program.description,
          ES: program.description,
          ZH: program.description,
          VI: program.description,
          FIL: program.description,
          KO: program.description,
          AR: program.description,
        },
        matchPercentage: Math.min(100, Math.round(matchPercentage)),
        matchCriteria,
        timestamp: Date.now(),
        aiProgram: program, // Store the full AI program data
      };
    });
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-10" />
          <div className="mt-48 sm:mt-20">
            <StepIndicator steps={steps} currentStep={2} />
          </div>
          
          <button
            onClick={() => navigate("/start-here")}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-32 sm:mt-12 mb-6"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {currentMessages.back}
          </button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              {currentMessages.findMatches}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              {currentMessages.answerQuestions}
            </p>
          </div>

            <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="age"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {currentMessages.age}
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    required
                  min="18"
                  max="120"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor="monthlyIncome"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {currentMessages.monthlyIncome}
                  </label>
                  <select
                    id="monthlyIncome"
                    name="monthlyIncome"
                    value={formData.monthlyIncome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  >
                    <option value="">
                      {currentMessages.selectIncome}
                    </option>
                    <option value="0-1000">$0 - $1,000</option>
                    <option value="1001-2000">$1,001 - $2,000</option>
                    <option value="2001-3000">$2,001 - $3,000</option>
                    <option value="3001-4000">$3,001 - $4,000</option>
                    <option value="4001-5000">$4,001 - $5,000</option>
                    <option value="5001+">$5,001+</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="householdSize"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {currentMessages.householdSize}
                  </label>
                  <select
                    id="householdSize"
                    name="householdSize"
                    value={formData.householdSize}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  >
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6+">6+</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="employmentStatus"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {currentMessages.employmentStatus}
                  </label>
                  <select
                    id="employmentStatus"
                    name="employmentStatus"
                    value={formData.employmentStatus}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  >
                    <option value="employed">
                      {currentMessages.employed}
                    </option>
                    <option value="unemployed">
                      {currentMessages.unemployed}
                    </option>
                    <option value="student">
                      {currentMessages.student}
                    </option>
                    <option value="retired">
                      {currentMessages.retired}
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="immigrationStatus"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {currentMessages.status}
                  </label>
                  <select
                    id="immigrationStatus"
                    name="immigrationStatus"
                    value={formData.immigrationStatus}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  >
                    <option value="citizen">
                      {currentMessages.citizen}
                    </option>
                    <option value="permanent">
                      {currentMessages.permanent}
                    </option>
                    <option value="other">
                      {currentMessages.other}
                    </option>
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  {currentMessages.additionalInfo}
                </h3>

                <div className="space-y-3">
                <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="receivesAid"
                      checked={formData.receivesAid}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {currentMessages.receivingAid}
                    </span>
                  </label>

                <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="hasDocuments"
                      checked={formData.hasDocuments}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {currentMessages.hasDocuments}
                    </span>
                  </label>

                <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="speaksEnglish"
                      checked={formData.speaksEnglish}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      {currentMessages.speaksEnglish}
                    </span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-lg"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {currentMessages.findingBestFit}
                  </div>
                ) : (
                  currentMessages.findMyMatches
                )}
              </button>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2 px-4">
                {currentMessages.loadingTime}
              </p>
            </form>
          </div>
        </div>
    </div>
  );
};

export default Match;